import {
  put,
  cancelled,
  take,
  fork,
  cancel,
  call,
  select,
  all, takeEvery
} from 'redux-saga/effects';
import { startSubmit, stopSubmit } from 'redux-form';

import createRequest from '../../api/httpRequest';
import SessionTypes from './SessionTypes';
import ErrorTypes from '../handler/ErrorTypes';
import StorageHelper from '../../utils/StorageHelper';
import { FORMS, REQUEST, STORAGE_KEYS, USER_CONFIG } from '../../configs/AppConfig';
import API_AUTHEN from '../../api/authentication';
import { cacheRoute, changeRoute } from '../route/RouteActions';
import { getCachedRoutePath, getHistory } from '../route/routeSelector';
import RouteTypes from '../route/RouteTypes';
import camelcaseKeys from 'camelcase-keys';
import Util from '../../utils/Util';
import { changeLocale } from '../locale/LocaleActions';
import CompanyTypes from '../company/CompanyTypes';
import { getCompanyDomain } from '../company/companySelector';
import NotificationTypes from '../notification/NotificationTypes';
import AccountTypes from '../account/AccountTypes';

let loginTask;

export default function * sessionSaga () {
  yield all([sessionLifeCycleSaga(), restoreSessionSaga(), loginOTP(), login(), checkActivity()]);
}

/**
 * Some rules:
 * - Token just stored in session storage [don't store in local storage]
 * - Credentials always stored in session storage [if remember login is true then store both session & local storage]
 */
function * sessionLifeCycleSaga () {
  while (true) {
    // init session
    const authToken = yield call(
      StorageHelper.getCookie,
      STORAGE_KEYS.session
    );
    if (authToken) {
      // there is token in session => validate token state
      yield fork(validateSession);
    } else {
      // // wait for login request...
      // const { payload } = yield take(SessionTypes.LOG_IN_REQUEST);
      // // notify for redux-form request is handling
      // yield put(startSubmit(FORMS.login));
      //
      // // fork return a Task object (a task with mission handle login request)
      // loginTask = yield fork(authorize, payload);
    }

    // after logged in, wait some another request [log out, reauthorize when token expired...]
    const action = yield take([
      SessionTypes.LOG_OUT,
      SessionTypes.REAUTHORIZE, // call API return status 401
      SessionTypes.LOG_IN_ERROR,
      SessionTypes.RESTORE_SESSION_FAILURE
    ]);
    switch (action.type) {
      case SessionTypes.LOG_OUT:
        yield call(logout);
        break;

      case SessionTypes.REAUTHORIZE:
      case SessionTypes.RESTORE_SESSION_FAILURE:
        yield clearToken();
        break;

      default:
      // don't do anything
    }
  }
}

function * logout () {
  // cancel login if request is handling
  if (loginTask) {
    yield cancel(loginTask);
  }

  yield createRequest({
    api: API_AUTHEN.LOG_OUT
  });

  yield clearState();
  yield clearToken();
}

function * clearToken () {
  // yield call(StorageHelper.removeSessionItem, STORAGE_KEYS.session);
  yield call(StorageHelper.removeCookie, STORAGE_KEYS.session);
  yield call(StorageHelper.removeLocalItem, STORAGE_KEYS.user);
  yield call(StorageHelper.removeLocalItem, STORAGE_KEYS.autoLogout);
  yield call(StorageHelper.removeLocalItem, STORAGE_KEYS.lastActivity);
  yield put(changeRoute('/login', ''));
  yield put(cacheRoute(null, null));
}

function * clearState () {
  const selected = [];
  const isSelected = false;
  yield put({
    type: CompanyTypes.UPDATE_SELECTED_ALL,
    payload: { selected, isSelected }
  });
  yield put({
    type: CompanyTypes.UPDATE_COMPANIES_QUERIES,
    payload: {
      page: 1,
      size: 10,
      search_key: ''
    }
  });
  yield put({
    type: AccountTypes.SET_SHOW_MODAL,
    payload: false
  });
}

/**
 * handle wrong domain
 * @param domain
 * @returns {Generator<SimpleEffect<"PUT", PutEffectDescriptor<{payload: {config: {autoDismiss: number, level: string, position: string, message: string}}, type: string}>>, void, *>}
 */
function * handleWrongDomain (domain) {
  let url;
  const protocol = window.location.protocol;
  const hash = window.location.hash;
  const port = window.location.port;
  const pathname = window.location.pathname;
  if (port) {
    url = protocol + '//' + domain + ':' + port + pathname + hash;
  } else {
    url = protocol + '//' + domain + pathname + hash;
  }
  yield put({
    type: NotificationTypes.SHOW_NOTIFICATION,
    payload: {
      config: {
        message: 'toast.error.wrongDomain',
        level: 'error',
        autoDismiss: 3,
        position: 'tc'
      }
    }
  });
  setTimeout(() => {
    window.location.href = url;
  }, 2000);
}

function * handleLoginSuccess (data) {
  const res = Util.toCamelCaseKey(data);
  const user = res.user;
  const currentDomain = yield select(getCompanyDomain);
  const domain = res.domain;
  if (currentDomain !== domain) {
    yield call(handleWrongDomain, domain);
  } else {
    if (user.roleLevel === USER_CONFIG.COMPANY_ADMIN.roleLevel) {
      StorageHelper.setLocalItem(STORAGE_KEYS.lastActivity, new Date().toISOString());
    }
    // store in local storage
    // yield call(StorageHelper.setSessionItem, STORAGE_KEYS.session, res.accessToken);
    yield call(StorageHelper.setCookie, STORAGE_KEYS.session, res.accessToken);
    yield call(StorageHelper.setLocalObject, STORAGE_KEYS.user, user);
    yield call(StorageHelper.setLocalItem, STORAGE_KEYS.autoLogout, res.timeSignOut);

    yield put(changeLocale(user.lang));
    yield put({ type: SessionTypes.LOG_IN_SUCCESS, payload: res });
    yield put(stopSubmit(FORMS.login));
  }
}

function * isResetPassword (accessToken) {
  if (accessToken) {
    yield call(StorageHelper.setCookie, STORAGE_KEYS.session, accessToken);
  }
  yield put(stopSubmit(FORMS.login));
}

function * login () {
  yield takeEvery(SessionTypes.LOG_IN_REQUEST, authorize);
}

function * authorize ({ payload: { email, password, rememberMe } }) {
  const domain = yield select(getCompanyDomain);
  yield createRequest({
    api: Object.assign({}, API_AUTHEN.LOG_IN, {
      data: {
        email: email,
        keep_login: rememberMe,
        password_hash: password,
        domain: domain
      }
    }),
    onSuccess: function * ({ data, status }, response) {
      if (data && status === 200) {
        yield call(handleLoginSuccess, data);
      } else {
        console.error('Can not read "access_token" from response');
      }
    },
    onError: function * (error, response) {
      console.log('error', error);
      yield put(stopSubmit(FORMS.login, { _error: 'api.error.' + error.status }));
      yield put({ type: SessionTypes.LOG_IN_ERROR });
      if (error.status === 614) {
        yield put(changeRoute('/login-step-2'));
      } else if (error.status === 615 || error.status === 616) {
        yield call(isResetPassword, error.data);
        yield put(changeRoute('/reset-password/' + `${error.status === 616 ? '2' : '1'}`));
      } else if (error.status === 719) {
        yield call(handleWrongDomain, error.data);
      } else {
        yield put({ type: ErrorTypes.API_RESPONSE_ERROR, response: response });
      }
    },
    onRequestError: function * (response) {
      console.log('response', response);
      yield put(stopSubmit(FORMS.login));
      yield put({ type: SessionTypes.LOG_IN_ERROR });
      yield put({ type: ErrorTypes.API_RESPONSE_ERROR, response: response });
    }
  });

  if (yield cancelled()) {
    // ... put special cancellation handling code here
  }
}

function * loginOTP () {
  yield takeEvery(SessionTypes.LOGIN_OTP_REQUEST, _loginOTP);
}

function * _loginOTP ({ payload }) {
  const domain = yield select(getCompanyDomain);
  yield createRequest({
    api: {
      ...API_AUTHEN.LOGIN_OTP,
      data: {
        ...payload,
        domain: domain
      }
    },
    onSuccess: function * ({ data, status }, response) {
      if (data && status === 200) {
        yield call(handleLoginSuccess, data);
      } else {
        console.error('Can not read "access_token" from response');
      }
    },
    onError: function * (error, response) {
      console.log('error', error);
      if (error.status === 615 || error.status === 616) {
        yield call(isResetPassword, error.data);
        yield put(changeRoute('/reset-password/' + `${error.status === 616 ? '2' : '1'}`));
      } else if (error.status === 719) {
        yield call(handleWrongDomain, error.data);
      } else {
        yield put(stopSubmit(FORMS.loginOTP, { _error: 'api.error.' + error.status }));
        yield put({ type: SessionTypes.LOG_IN_ERROR });
        yield put({ type: ErrorTypes.API_RESPONSE_ERROR, response: response });
      }
    },
    onRequestError: function * (response) {
      console.log('response', response);
      yield put(stopSubmit(FORMS.login));
      yield put({ type: SessionTypes.LOG_IN_ERROR });
      yield put({ type: ErrorTypes.API_RESPONSE_ERROR, response: response });
    }
  });
}

function * checkActivity () {
  yield takeEvery(SessionTypes.CHECK_ACTIVITY, _checkActivity);
}

function * _checkActivity () {
  yield createRequest({ api: API_AUTHEN.GET_AUTH_INFO });
}

function * validateSession () {
  yield createRequest({
    api: API_AUTHEN.GET_AUTH_INFO,
    onSuccess: function * (data, response) {
      const user = camelcaseKeys(data.data.user);
      const domain = data.data.domain;
      // update token
      const authToken = response.config.headers[REQUEST.ACCESS_HEADER];
      // yield call(StorageHelper.setSessionItem, STORAGE_KEYS.session, authToken);
      yield call(StorageHelper.setCookie, STORAGE_KEYS.session, authToken);
      yield call(
        StorageHelper.setLocalObject,
        STORAGE_KEYS.user,
        user
      );
      yield put(changeLocale(user.lang));
      yield put({ type: SessionTypes.RESTORE_SESSION_SUCCESS, payload: user });
      yield put({ type: CompanyTypes.SET_COMPANY_DOMAIN, payload: domain });
    },
    onError: function * () {
      // cache route to restore session after login
      yield put({ type: SessionTypes.CACHE_PREV_LOGIN_ROUTE });
      yield put({ type: SessionTypes.RESTORE_SESSION_FAILURE });
    }
  });

  if (yield cancelled()) {
    // ... put special cancellation handling code here
  }
}

/**
 * There are 3 place dispatch `SessionTypes.CACHE_PREV_LOGIN_ROUTE` action
 * 1. errorSaga - handleApiError(): in case API return status code 401
 * 2. sessionSaga - validateSession(): in case validate session failure [token is invalid or expired]
 * 3. AuthenticatedRoute component - getDerivedStateFromProps(): in case access a router required authenticated but not yet authenticated
 */
// restore the last accessed route after login
function * restoreSessionSaga () {
  // only active after set history
  yield take(RouteTypes.SET_HISTORY);
  while (true) {
    const { pathname } = yield select(getCachedRoutePath);
    if (!pathname) {
      // update current route before redirect to login page
      yield take(SessionTypes.CACHE_PREV_LOGIN_ROUTE);

      // get current route
      const {
        location: { pathname, search }
      } = yield select(getHistory);
      yield put(cacheRoute(pathname, search));
    }

    const action = yield take([
      SessionTypes.LOG_IN_SUCCESS,
      SessionTypes.REAUTHORIZE, // call API return status 401
      SessionTypes.RESTORE_SESSION_FAILURE
    ]);
    switch (action.type) {
      case SessionTypes.LOG_IN_SUCCESS:
        // restore the last route
        const { pathname, search } = yield select(getCachedRoutePath);
        console.log('getCachedRoutePath', pathname);
        if (pathname) {
          yield put(changeRoute(pathname, search));
          yield put(cacheRoute(null, null));
        }
        break;

      case SessionTypes.REAUTHORIZE:
      case SessionTypes.RESTORE_SESSION_FAILURE:
        yield clearToken();
        break;

      default:
        break;
    }
  }
}
